import { QueryStatus } from "../../app/types";
import { RemoteMessage } from "./messageParser";
import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../../app/store";
import { fetchRemoteMessages } from "../Firebase/firebaseUtils";

/**
 * Stable hash of startTime, endTime and body. Used as the dismissal storage key
 * for dismissible messages so that if the message content or window changes at
 * source, the user sees it again (different hash = not dismissed).
 */
export const getDismissalKey = (msg: RemoteMessage): string => {
  const input = `${msg.startTime}|${msg.endTime}|${msg.body}`;
  let h1 = 5381;
  let h2 = 33;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = (h1 * 33) ^ c;
    h2 = (h2 * 31) ^ c;
  }
  return (
    (h1 >>> 0).toString(16).padStart(8, "0") +
    (h2 >>> 0).toString(16).padStart(8, "0")
  );
};

type MessageQueueState = {
  messages: RemoteMessage[];
  status: QueryStatus;
  error?: string;
  dismissed: Record<string, string>;
};

const readDismissals = (): Record<string, string> => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const stored = window.localStorage.getItem("mq_dismissed");
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const writeDismissals = (dismissed: Record<string, string>) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem("mq_dismissed", JSON.stringify(dismissed));
  } catch {
    // ignore storage failures
  }
};

const localDismissals = readDismissals();

const initialState: MessageQueueState = {
  messages: [],
  status: "idle",
  dismissed: localDismissals
};

export const messageQueueSlice = createSlice({
  name: "messageQueue",
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<QueryStatus>) => {
      state.status = action.payload;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setMessages: (state, action: PayloadAction<RemoteMessage[]>) => {
      const now = Date.now();
      const active = action.payload.filter(
        (msg) => new Date(msg.endTime).getTime() > now
      );

      const nonDismissible = active
        .filter((msg) => !msg.dismissible)
        .sort((a, b) =>
          a.priority !== b.priority
            ? a.priority - b.priority
            : new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );

      if (nonDismissible.length > 0) {
        state.messages = nonDismissible;
        return;
      }

      const dismissible = active
        .filter((msg) => msg.dismissible)
        .sort((a, b) =>
          a.priority !== b.priority
            ? a.priority - b.priority
            : new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );

      state.messages = dismissible;
    },
    dismissMessage: (state, action: PayloadAction<RemoteMessage>) => {
      const msg = action.payload;
      if (!msg.dismissible) return;
      const key = getDismissalKey(msg);
      state.dismissed[key] = msg.endTime;
      writeDismissals(state.dismissed);
    }
  }
});

export const {
  setStatus: setMessageStatus,
  setError: setMessageError,
  setMessages,
  dismissMessage
} = messageQueueSlice.actions;

const selectMessageQueue = (state: RootState) => state.messageQueue;
const selectMessages = (state: RootState) => state.messageQueue.messages;
const selectDismissed = (state: RootState) => state.messageQueue.dismissed;

export const selectActiveMessages = createSelector(
  [selectMessages, selectDismissed],
  (messages, dismissed) => {
    const now = Date.now();
    return messages.filter((msg) => {
      const start = new Date(msg.startTime).getTime();
      const end = new Date(msg.endTime).getTime();
      if (now < start || now > end) return false;
      if (msg.dismissible) {
        const key = getDismissalKey(msg);
        const dismissedEnd = dismissed[key];
        if (dismissedEnd === msg.endTime) return false;
      }
      return true;
    });
  }
);

export const selectBlockingMessages = createSelector(
  [selectActiveMessages],
  (active) => active.filter((msg) => !msg.dismissible)
);

export const refreshMessageQueue = () => async (dispatch: AppDispatch) => {
  dispatch(setMessageStatus("loading"));
  dispatch(setMessageError(undefined));
  try {
    const remoteMessages = await fetchRemoteMessages();
    dispatch(setMessages(remoteMessages));
    dispatch(setMessageStatus("succeeded"));
  } catch (error) {
    dispatch(setMessageStatus("failed"));
    dispatch(
      setMessageError(
        error instanceof Error ? error.message : "Unknown message queue error"
      )
    );
  }
};

export default messageQueueSlice.reducer;


